import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '../../api/client';
import type { Session } from '../../api/session';
import { getRequest, getRequestMessages, sendRequestMessage, withdrawRequest } from '../../api/tenant';
import { moscowDayLabel, moscowTime, parseServerDate } from '../../domain/moscowTime';
import { describePassError } from '../../domain/passErrors';
import { describeRequest } from '../../domain/requests';
import type { TelegramApi } from '../../telegram/webapp';
import { Skeleton } from '../../ui/Skeleton';
import { LoadFailed } from '../common/LoadFailed';
import c from '../pass-card/passCard.module.css';
import s from './requests.module.css';

const POLL_MS = 10_000;

/** One request and its conversation; polls every 10 s while open (spec §6.7). */
export function RequestChatScreen({ session, telegram }: { session: Session; telegram: TelegramApi }) {
  const id = Number(useParams().id);
  const queryClient = useQueryClient();
  const request = useQuery({ queryKey: ['request', id], queryFn: () => getRequest(session, id) });
  const view = request.data ? describeRequest(request.data) : null;
  const messages = useQuery({
    queryKey: ['request', id, 'messages'],
    queryFn: () => getRequestMessages(session, id),
    refetchInterval: view?.open ? POLL_MS : false,
  });
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const send = useMutation({
    mutationFn: () => sendRequestMessage(session, id, text.trim()),
    onSuccess: (msg) => {
      setText('');
      queryClient.setQueryData(['request', id, 'messages'], (old: typeof messages.data) => [msg, ...(old ?? [])]);
    },
    onError: (e) => {
      telegram.notify('error');
      setError(describePassError(e).general);
      if (e instanceof ApiError && e.code === 'REQUEST_IS_CLOSED') void request.refetch();
    },
  });

  const withdraw = useMutation({
    mutationFn: () => withdrawRequest(session, id),
    onSuccess: () => {
      setConfirming(false);
      void request.refetch();
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
    onError: (e) => {
      setConfirming(false);
      setError(describePassError(e).general);
    },
  });

  if (request.isError) return <LoadFailed error={request.error} onRetry={() => void request.refetch()} fullScreen />;
  if (!request.data || !view) {
    return (
      <main className={c.card} aria-busy="true">
        <Skeleton height={100} />
      </main>
    );
  }

  const ordered = [...(messages.data ?? [])].reverse();
  const now = new Date();

  return (
    <main className={`${c.card} ${s.chat}`}>
      <header className={c.head}>
        <span className={s.kind}>{view.kind}</span>
        <h1 className={c.title}>{view.title}</h1>
        {view.subtitle && <p className={c.meta}>{view.subtitle}</p>}
        <p className={c.meta}>{view.status}</p>
        {view.reason && <p className={s.reason}>{view.reason}</p>}
      </header>

      {messages.isPending ? (
        <Skeleton height={120} />
      ) : (
        <ul className={s.messages} aria-label="Переписка">
          {ordered.map((m) => {
            const mine = m.sender?.type === 'tenant';
            const at = parseServerDate(m.createdAt);
            return (
              <li key={m.id} className={mine ? `${s.bubble} ${s.mine}` : s.bubble}>
                {!mine && <span className={s.author}>{m.sender?.name || 'УК'}</span>}
                <span className={s.text}>{m.text}</span>
                <span className={s.stamp}>
                  {at ? `${moscowDayLabel(at, now)} ${moscowTime(at)}` : ''}
                  {mine && m.isRead ? ' · Прочитано' : ''}
                </span>
              </li>
            );
          })}
          {ordered.length === 0 && <li className={s.emptyChat}>Сообщений пока нет. УК ответит здесь.</li>}
        </ul>
      )}

      {error && (
        <p className={s.error} role="alert">
          {error}
        </p>
      )}

      {view.open ? (
        <>
          <form
            className={s.composer}
            onSubmit={(e) => {
              e.preventDefault();
              if (!text.trim()) return;
              setError(null);
              send.mutate();
            }}
          >
            <label htmlFor="chat-input" className={s.srOnly}>
              Сообщение в УК
            </label>
            <textarea id="chat-input" rows={2} value={text} maxLength={4000} onChange={(e) => setText(e.target.value)} placeholder="Сообщение" />
            <button type="submit" disabled={send.isPending || !text.trim()}>
              Отправить
            </button>
          </form>
          {confirming ? (
            <div className={c.confirm} role="group" aria-label="Подтверждение">
              <p className={c.note}>УК больше не будет рассматривать это обращение.</p>
              <button type="button" className={c.danger} onClick={() => withdraw.mutate()} disabled={withdraw.isPending}>
                Да, отозвать
              </button>
              <button type="button" className={c.ghost} onClick={() => setConfirming(false)}>
                Нет
              </button>
            </div>
          ) : (
            <button type="button" className={c.dangerLink} onClick={() => setConfirming(true)}>
              Отозвать обращение
            </button>
          )}
        </>
      ) : (
        <p className={c.note}>Обращение закрыто — написать в него нельзя</p>
      )}
    </main>
  );
}
